import { Events } from '@/services/Events';
import { TextureLoader, CubeTextureLoader, EquirectangularReflectionMapping } from 'three';
// @ts-expect-error import working but lint issue
import { EXRLoader } from 'three/addons/loaders/EXRLoader';
// @ts-expect-error import working but lint issue
import { RGBELoader } from 'three/addons/loaders/RGBELoader';
// @ts-expect-error import working but lint issue
import { RGBMLoader } from 'three/addons/loaders/RGBMLoader';
// @ts-expect-error import working but lint issue
import { HDRCubeTextureLoader } from 'three/addons/loaders/HDRCubeTextureLoader';
import type { Texture } from 'three';
import type { EnvironmentParams } from '@/commons/environment/EnvironmentMapParams';
import type { ILoadedEnvironmentMap } from '@/commons/environment/ILoadedEnvironmentMap';

class EnvironmentMapLoader {
    private static _instance: EnvironmentMapLoader | null;

    private _loaderUtils = {
        threeTextureLoader: new TextureLoader(),
        threeEXRLoader: new EXRLoader(),
        threeRGBELoader: new RGBELoader(),
        threeRGBMLoader: new RGBMLoader(),
        threeCubeTextureLoader: new CubeTextureLoader(),
        threeHDRCubeTextureLoader: new HDRCubeTextureLoader(),
    };

    private _environments: [ILoadedEnvironmentMap | null] = [null];

    private static get getInstance(): EnvironmentMapLoader {
        if (!EnvironmentMapLoader._instance) {
            EnvironmentMapLoader._instance = new EnvironmentMapLoader();
        }

        return EnvironmentMapLoader._instance;
    }

    static loadEquirectangular(path: string, environmentParams: EnvironmentParams): Promise<Texture> {
        return EnvironmentMapLoader.getInstance._loadEquirectangular(path, environmentParams);
    }

    static loadCube(pathList: string[], environmentParams: EnvironmentParams): Promise<Texture> {
        return EnvironmentMapLoader.getInstance._loadCube(pathList, environmentParams);
    }

    static getNameFromPath(path: string) {
        const name = path?.split('/').pop();
        if (!name) {
            return '';
        }
        return name.split('.')[0];
    }

    static getExtensionFromPath(path: string) {
        const name = path?.split('/').pop();
        if (!name) {
            return '';
        }
        return name.split('.').pop();
    }

    private _loadEquirectangular(path: string, environmentParams: EnvironmentParams): Promise<Texture> {
        const name = EnvironmentMapLoader.getNameFromPath(path);
        const extension = EnvironmentMapLoader.getExtensionFromPath(path);
        const isEnvironmentLoaded = this._environments.find((loadedEnvironment) => loadedEnvironment?.name === name);
        if (isEnvironmentLoaded) {
            return isEnvironmentLoaded.promise;
        }
        const loadedEnvironment = {
            name,
        } as ILoadedEnvironmentMap;

        loadedEnvironment.promise = new Promise((resolve, reject) => {
            Events.emit('loading-start', name);

            if (environmentParams.environmentType === 'HDR') {
                if (extension === 'hdr') {
                    this._loaderUtils.threeRGBELoader.load(
                        path,
                        (texture: Texture) => {
                            texture.mapping = EquirectangularReflectionMapping;
                            resolve(texture);
                        },
                        // onProgress is not supported at current time
                        undefined,
                        (errorMsg: unknown) => {
                            reject(new Error(errorMsg as string));
                        },
                    );
                } else {
                    this._loaderUtils.threeRGBELoader.load(
                        path,
                        (texture: Texture) => {
                            texture.mapping = EquirectangularReflectionMapping;
                        },
                        // onProgress is not supported at current time
                        undefined,
                        (errorMsg: unknown) => {
                            reject(new Error(errorMsg as string));
                        },
                    );
                }
            }
        });

        loadedEnvironment.promise.then(() => {
            Events.emit('loading-end', name);
        });
        this._environments.push(loadedEnvironment);

        return loadedEnvironment.promise;
    }

    // TODO: store environment in environment list to avoid loading it again
    private _loadCube(pathList: string[], environmentParams: EnvironmentParams): Promise<Texture> {
        const path = pathList[0];
        const name = path ? path.split('/').slice(-2, -1)[0] : '';
        const extension = EnvironmentMapLoader.getExtensionFromPath(pathList[0]);
        const isEnvironmentLoaded = this._environments.find((loadedEnvironment) => loadedEnvironment?.name === name);
        if (isEnvironmentLoaded) {
            return isEnvironmentLoaded.promise;
        }
        const loadedEnvironment = {
            name,
        } as ILoadedEnvironmentMap;

        loadedEnvironment.promise = new Promise((resolve, reject) => {
            Events.emit('loading-start', name);

            if (environmentParams.environmentType === 'HDR' && extension === 'hdr') {
                this._loaderUtils.threeHDRCubeTextureLoader.load(
                    pathList,
                    (texture: Texture) => {
                        resolve(texture);
                    },
                    // onProgress is not supported at current time
                    undefined,
                    (errorMsg: unknown) => {
                        reject(new Error(errorMsg as string));
                    },
                );
            } else if (environmentParams.environmentType === 'LDR') {
                this._loaderUtils.threeCubeTextureLoader.load(
                    pathList,
                    (texture: Texture) => {
                        resolve(texture);
                    },
                    // onProgress is not supported at current time
                    undefined,
                    (errorMsg: unknown) => {
                        reject(new Error(errorMsg as string));
                    },
                );
            } else if (environmentParams.environmentType === 'RGBM') {
                this._loaderUtils.threeRGBMLoader.setMaxRange(16).loadCubemap(
                    pathList,
                    (texture: Texture) => {
                        resolve(texture);
                    },
                    // onProgress is not supported at current time
                    undefined,
                    (errorMsg: unknown) => {
                        reject(new Error(errorMsg as string));
                    },
                );
            }
        });

        loadedEnvironment.promise.then(() => {
            Events.emit('loading-end', name);
        });
        this._environments.push(loadedEnvironment);

        return loadedEnvironment.promise;
    }

    static init() {
        return EnvironmentMapLoader.getInstance;
    }

    static dispose() {
        EnvironmentMapLoader._instance = null;
    }
}

export { EnvironmentMapLoader };
